import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';
import { spawnPickup } from './pickups.js';
import { colliders } from './colliders.js';
import { COMM_PARTS } from '../game/commParts.js';

// Alien ruins — a stone altar with three sockets, guarded by standing
// stones. Each socket wants a specific coloured crystal; placing all
// three reveals the Antenna Array comm part. The POI is also a first
// taste of "items as keys" — the crystals are no longer just clutter.
//
// Emits:
//   'ruins:discovered'   — player first enters the ring
//   'ruins:socket-filled' ({ socketId })
//   'ruins:solved'       — final socket filled; antenna spawns next tick

const UP_Y = new THREE.Vector3(0, 1, 0);
// Southern-hemisphere latitude −30°, longitude 180°. Sits on the
// 3-POI triangle with the pond and cave, 120° of longitude apart.
export const RUINS_DIRECTION = new THREE.Vector3(
  Math.cos(-30 * Math.PI / 180) * Math.cos(180 * Math.PI / 180),
  Math.sin(-30 * Math.PI / 180),
  Math.cos(-30 * Math.PI / 180) * Math.sin(180 * Math.PI / 180),
).normalize();

const DISCOVERY_RANGE        = 4.5;
const SOCKET_INTERACT_RANGE  = 1.3;

// Socket definitions: one per crystal colour. The angle places it on the
// altar top; the world position is computed after the group is placed.
const SOCKETS = [
  { id: 'socket_amber', requiredId: 'crystal_amber', crystalName: 'Amber Crystal', color: 0xffcf50, angle: -Math.PI / 2 },
  { id: 'socket_azure', requiredId: 'crystal_azure', crystalName: 'Azure Crystal', color: 0x3ae0ff, angle: -Math.PI / 2 + (2 * Math.PI / 3) },
  { id: 'socket_rose',  requiredId: 'crystal_rose',  crystalName: 'Rose Crystal',  color: 0xff5aa0, angle: -Math.PI / 2 + (4 * Math.PI / 3) },
];

let ruinsGroup   = null;
let ruinsWorldPos = null;
let sceneRef     = null;
let planetRef    = null;
let discovered   = false;
let solved       = false;
let solvedAt     = -1;         // performance-time of solve for animation

// Altar-top pieces — one wedge per socket. On solve they slide outward,
// carrying their socket + placed crystal so the altar visibly cracks open.
const altarPieces = [];

// Pit visuals revealed when the altar cracks open (dark stone disc + a
// pulsing energy ring that reads as raw power emerging from beneath).
let pitMesh  = null;
let pitRing  = null;
let pitGlowDisc = null;

export function createRuins(scene, planet) {
  sceneRef  = scene;
  planetRef = planet;
  ruinsGroup = new THREE.Group();

  const groundR = groundRadius(planet, RUINS_DIRECTION);
  ruinsGroup.quaternion.setFromUnitVectors(UP_Y, RUINS_DIRECTION);
  ruinsGroup.position.copy(RUINS_DIRECTION).multiplyScalar(groundR);
  scene.add(ruinsGroup);

  // ---- Materials ----
  const stoneMat = new THREE.MeshStandardMaterial({
    color: 0x585e68, roughness: 0.95, flatShading: true,
  });
  const darkStoneMat = new THREE.MeshStandardMaterial({
    color: 0x3a3e46, roughness: 0.95, flatShading: true,
  });
  const altarMat = new THREE.MeshStandardMaterial({
    color: 0x6a7078, roughness: 0.85, flatShading: true,
  });
  const groundMat = new THREE.MeshStandardMaterial({
    color: 0x5a4a34, roughness: 1, flatShading: true,
  });

  // ---- Weathered ground ring ----
  const groundRing = new THREE.Mesh(
    new THREE.CircleGeometry(2.4, 48), groundMat,
  );
  groundRing.rotation.x = -Math.PI / 2;
  groundRing.position.y = -0.02;
  groundRing.receiveShadow = true;
  ruinsGroup.add(groundRing);

  // Helper: register a cylinder collider for a solid structure in this
  // group. Converts the local base centre to world coords.
  const _tmpLocal = new THREE.Vector3();
  function addCollider(localX, localZ, radius, height) {
    _tmpLocal.set(localX, 0, localZ).applyQuaternion(ruinsGroup.quaternion);
    colliders.push({
      pos: ruinsGroup.position.clone().add(_tmpLocal),
      up:  RUINS_DIRECTION.clone(),
      r:   radius,
      h:   height,
    });
  }

  // ---- Central altar ----
  // Wider base, narrower top — classical stone plinth.
  const altarBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.9, 1.0, 0.38, 8),
    darkStoneMat,
  );
  altarBase.position.y = 0.19;
  altarBase.castShadow = true;
  altarBase.receiveShadow = true;
  ruinsGroup.add(altarBase);

  // Altar collider — widest base radius, covers the full plinth height.
  addCollider(0, 0, 1.0, 0.7);

  const altarMid = new THREE.Mesh(
    new THREE.CylinderGeometry(0.8, 0.9, 0.18, 8),
    altarMat,
  );
  altarMid.position.y = 0.47;
  altarMid.castShadow = true;
  ruinsGroup.add(altarMid);

  // ---- Altar top split into three wedges, one per socket ----
  // Each wedge is a child of its own piece group; the piece group holds
  // both the wedge mesh and its socket well, so on solve we can slide
  // the whole thing outward and the socket/crystal follow the stone.
  altarPieces.length = 0;
  const SOCKET_R = 0.38;       // socket radial distance from altar centre
  const SOCKET_Y = 0.64;       // just above altar top
  const WEDGE_ARC = (Math.PI * 2) / 3;

  for (const s of SOCKETS) {
    const pieceGroup = new THREE.Group();
    ruinsGroup.add(pieceGroup);

    const wedgeGeo = new THREE.CylinderGeometry(
      0.75, 0.8, 0.08, 10, 1, false,
      s.angle - WEDGE_ARC / 2, WEDGE_ARC,
    );
    const wedge = new THREE.Mesh(wedgeGeo, altarMat);
    wedge.position.y = 0.6;
    wedge.castShadow = true;
    pieceGroup.add(wedge);

    // Socket well on top of the wedge.
    const wellMat = new THREE.MeshStandardMaterial({
      color: 0x1a1c24, roughness: 0.8,
      emissive: s.color, emissiveIntensity: 0.25,
    });
    const well = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.085, 0.06, 16), wellMat,
    );
    const sx = Math.cos(s.angle) * SOCKET_R;
    const sz = Math.sin(s.angle) * SOCKET_R;
    well.position.set(sx, SOCKET_Y, sz);
    pieceGroup.add(well);

    s.mesh        = well;
    s.wellMat     = wellMat;
    s.localPos    = new THREE.Vector3(sx, SOCKET_Y, sz);
    s.worldPos    = new THREE.Vector3();
    s.filled      = false;
    s.crystalMesh = null;
    s.pieceGroup  = pieceGroup;

    altarPieces.push({
      group: pieceGroup,
      centerAngle: s.angle,
      // Tangent axis (perpendicular to the piece's outward direction) —
      // rotating around this tilts the outer edge downward.
      tiltAxis: new THREE.Vector3(
        -Math.sin(s.angle), 0, Math.cos(s.angle),
      ).normalize(),
    });
  }

  // ---- Pit visuals (hidden until solve) ----
  // Dark stone disc covering the altar mid so the gap reads as broken
  // interior rather than a clean surface.
  pitMesh = new THREE.Mesh(
    new THREE.CircleGeometry(0.7, 32),
    new THREE.MeshStandardMaterial({
      color: 0x0a0c12, roughness: 1, flatShading: true,
    }),
  );
  pitMesh.rotation.x = -Math.PI / 2;
  pitMesh.position.y = 0.565;
  pitMesh.visible = false;
  ruinsGroup.add(pitMesh);

  // Warm additive ring — pulses around the antenna as it emerges.
  pitRing = new THREE.Mesh(
    new THREE.RingGeometry(0.25, 0.55, 48),
    new THREE.MeshBasicMaterial({
      color: 0xfff2d8,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  pitRing.rotation.x = -Math.PI / 2;
  pitRing.position.y = 0.575;
  ruinsGroup.add(pitRing);

  // Soft warm inner disc — gives the pit centre a glow rather than a
  // sharp black hole.
  pitGlowDisc = new THREE.Mesh(
    new THREE.CircleGeometry(0.3, 32),
    new THREE.MeshBasicMaterial({
      color: 0xffe0a0,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  pitGlowDisc.rotation.x = -Math.PI / 2;
  pitGlowDisc.position.y = 0.57;
  ruinsGroup.add(pitGlowDisc);

  // ---- Three standing stones around the altar ----
  // Placed between sockets (offset half a slot round) so they frame the
  // altar rather than hiding it.
  const STANDING_R = 2.0;
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI / 3) + (Math.PI / 3);
    const h = 1.5 + Math.random() * 0.25;
    const sx = Math.cos(a) * STANDING_R;
    const sz = Math.sin(a) * STANDING_R;
    const stone = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.55, 0), darkStoneMat,
    );
    stone.position.set(sx, h * 0.5 + 0.05, sz);
    stone.scale.set(0.7, h / 0.9, 0.65);
    stone.rotation.y = Math.random() * Math.PI * 2;
    stone.rotation.z = (Math.random() - 0.5) * 0.22;
    stone.castShadow = true;
    ruinsGroup.add(stone);

    // Collider — radius slightly smaller than the visible width so the
    // player can just barely squeeze past, height matches the stone.
    addCollider(sx, sz, 0.42, h);
  }

  // ---- Rubble scattered across the ring ----
  for (let i = 0; i < 9; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 1.0 + Math.random() * 1.2;
    const size = 0.1 + Math.random() * 0.14;
    const rubble = new THREE.Mesh(
      new THREE.DodecahedronGeometry(size), stoneMat,
    );
    rubble.position.set(
      Math.cos(a) * r,
      -0.02 + size * 0.3,
      Math.sin(a) * r,
    );
    rubble.rotation.set(Math.random() * 2, Math.random() * 2, Math.random() * 2);
    rubble.castShadow = true;
    ruinsGroup.add(rubble);
  }

  // Compute world positions for the sockets now that the group is placed.
  // Sockets live under their piece group, which sits at origin inside
  // the ruins group until solved — so localToWorld walks through both.
  ruinsGroup.updateMatrixWorld(true);
  for (const s of SOCKETS) {
    s.worldPos.copy(s.localPos);
    s.pieceGroup.localToWorld(s.worldPos);
  }

  ruinsWorldPos = ruinsGroup.position.clone();
  return { group: ruinsGroup, worldPos: ruinsWorldPos };
}

export function updateRuins(character) {
  if (!ruinsGroup) return;

  // Discovery beat.
  if (!discovered && ruinsWorldPos) {
    const dist = character.position.distanceTo(ruinsWorldPos);
    if (dist < DISCOVERY_RANGE) {
      discovered = true;
      events.emit('ruins:discovered', { position: ruinsWorldPos.clone() });
    }
  }

  const t = performance.now() / 1000;
  for (const s of SOCKETS) {
    if (!s.filled) {
      // Breathing pulse on unfilled sockets — indicates they're still
      // "hungry" for a crystal.
      const pulse = 0.2 + 0.3 * (0.5 + 0.5 * Math.sin(t * 1.6 + s.angle));
      s.wellMat.emissiveIntensity = pulse;
      continue;
    }

    // Placed crystal — slow spin, gentle bob, and emissive pulse. Each
    // socket has its own phase so they're not lockstep.
    const c = s.crystalMesh;
    if (!c) continue;
    const p = s.crystalPhase;
    c.rotation.y = t * 0.8 + p;
    c.rotation.x = Math.sin(t * 0.6 + p) * 0.12;
    c.position.y = s.crystalBaseY + Math.sin(t * 1.4 + p) * 0.04;
    s.crystalMat.emissiveIntensity = 0.8 + 0.35 * Math.sin(t * 1.8 + p);
  }

  // Crack animation: pieces slide outward, tilt their outer edge down,
  // and a warm glow blooms from the pit in the middle. Eased one-shot.
  if (solvedAt > 0 && altarPieces.length) {
    const DURATION = 1.6;
    const elapsed = t - solvedAt;
    const p = Math.min(1, Math.max(0, elapsed / DURATION));
    const eased = 1 - Math.pow(1 - p, 3);           // easeOutCubic
    const OUT  = 0.75;                              // radial travel
    const DROP = 0.08;                              // sink as they spread
    const TILT = -0.45;                             // outer edge rotates down

    for (const piece of altarPieces) {
      piece.group.position.x = Math.cos(piece.centerAngle) * OUT * eased;
      piece.group.position.z = Math.sin(piece.centerAngle) * OUT * eased;
      piece.group.position.y = DROP * eased * -1;
      piece.group.quaternion.setFromAxisAngle(piece.tiltAxis, TILT * eased);
    }

    // Pit visuals bloom in over the first half of the animation, then hold.
    const reveal = Math.min(1, eased * 1.8);
    if (pitMesh)      pitMesh.visible = reveal > 0.05;
    if (pitRing)      pitRing.material.opacity = 0.55 * reveal;
    if (pitGlowDisc)  pitGlowDisc.material.opacity = 0.45 * reveal;

    if (p >= 1) solvedAt = -1;                      // freeze final pose
  }

  // Continuous idle pulse on the pit ring once it's revealed — subtle
  // life in the altar even after the crack animation finishes.
  if (pitRing && pitRing.material.opacity > 0) {
    const baseOp = 0.55;
    const breathe = 0.5 + 0.5 * Math.sin(t * 1.8);
    pitRing.material.opacity    = baseOp * (0.8 + 0.2 * breathe);
    if (pitGlowDisc) pitGlowDisc.material.opacity = 0.45 * (0.85 + 0.15 * breathe);
  }
}

// Returns the closest unfilled socket within interact range, or null.
export function getNearbySocket(worldPos) {
  if (!ruinsGroup || solved) return null;
  let best = null;
  let bestD = SOCKET_INTERACT_RANGE * SOCKET_INTERACT_RANGE;
  for (const s of SOCKETS) {
    if (s.filled) continue;
    const d = s.worldPos.distanceToSquared(worldPos);
    if (d < bestD) { best = s; bestD = d; }
  }
  return best;
}

// Drop a crystal into the socket — places a visible gem and steady glow.
export function fillSocket(socketId) {
  const s = SOCKETS.find((x) => x.id === socketId);
  if (!s || s.filled) return false;
  s.filled = true;
  s.wellMat.emissiveIntensity = 1.2;

  const crystalMat = new THREE.MeshStandardMaterial({
    color: s.color,
    emissive: s.color,
    emissiveIntensity: 0.85,
    metalness: 0.35, roughness: 0.25,
  });
  const crystal = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.085, 0), crystalMat,
  );
  crystal.position.copy(s.localPos);
  crystal.position.y += 0.14;
  crystal.castShadow = true;
  // Attach to the piece group so the crystal rides its wedge when the
  // altar cracks apart.
  s.pieceGroup.add(crystal);
  s.crystalMesh      = crystal;
  s.crystalMat       = crystalMat;
  s.crystalBaseY     = crystal.position.y;
  s.crystalPhase     = Math.random() * Math.PI * 2;

  events.emit('ruins:socket-filled', { socketId });

  if (SOCKETS.every((x) => x.filled)) {
    solved = true;
    solvedAt = performance.now() / 1000;
    events.emit('ruins:solved');

    // Spawn the Antenna Array pickup floating at the altar top — right
    // in the gap where the wedges split apart. We still use spawnPickup
    // for the halo + beacon wiring, then override the group's transform
    // so it sits above the altar rather than on the ground.
    const antennaPart = COMM_PARTS.find((p) => p.id === 'comm_antenna');
    if (antennaPart) {
      const entry = spawnPickup(sceneRef, planetRef, {
        ...antennaPart, beacon: true, dir: RUINS_DIRECTION.clone(),
      });
      const altarTopLocal = new THREE.Vector3(0, 0.6, 0);
      const altarTopWorld = altarTopLocal
        .applyQuaternion(ruinsGroup.quaternion)
        .add(ruinsGroup.position);
      entry.group.position.copy(altarTopWorld);
      entry.group.quaternion.setFromUnitVectors(UP_Y, RUINS_DIRECTION);
      entry.worldPos.copy(altarTopWorld);
    }
  }
  return true;
}
